import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  CalendarDays,
  ChevronLeft,
  Ellipsis,
  Eraser,
  MapPin,
  Merge,
  Pencil,
  Phone,
  SearchX,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
} from 'lucide-react'
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react'
import {
  clientQuery,
  consentsQuery,
  duplicatesQuery,
  notesQuery,
  timelineQuery,
  useAddNote,
  useEraseClient,
  useMergeClients,
  useRecordConsent,
  useUpdateClient,
} from '@/api/clients'
import { ApiError } from '@/api/client'
import { branchesQuery, membersQuery } from '@/api/queries'
import type { Access, Branch, Client, ClientNote, ConsentEvent, ConsentPurpose, DuplicateCandidate } from '@/api/types'
import { ClientVisits, InsideNowFact } from '@/app/access/client-visits'
import { EditClientPanel } from '@/app/client-form'
import { ClientPassesSlot } from '@/app/client-passes-slot'
import { ClientPurchases } from '@/app/goods/client-purchases'
import { useCompanyCaller } from '@/app/company'
import { FaceEntry } from '@/app/face-entry'
import { ClientLocker } from '@/app/access/client-locker'
import { canEnrolFace } from '@/domain/face-photo'
import { NothingForRole } from '@/app/nothing-for-role'
import { clientAbilities, placeableBranches, type ClientAbilities } from '@/domain/client-abilities'
import { ageOn, clientName, formatPhone, latestConsent, timelineText, todayIso } from '@/domain/clients'
import { clientErrorText, errorText } from '@/domain/errors'
import { atLeast } from '@/domain/permissions'
import { formatDate, t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { Avatar, CompactRow } from '@/ui/compact-row'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem } from '@/ui/overlays'
import { EmptyState, Page } from '@/ui/page'
import { NeutralSegmented } from '@/ui/segmented'
import { SectionHeader } from '@/ui/section-header'
import { SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'
import { Switch } from '@/ui/switch'

type CardSearch = { created?: 'ok' | 'consents_failed' }

export const Route = createFileRoute('/_app/c/$orgId/clients/$clientId')({
  validateSearch: (raw: Record<string, unknown>): CardSearch =>
    raw['created'] === 'ok' || raw['created'] === 'consents_failed' ? { created: raw['created'] } : {},
  component: ClientPage,
})

const c = t.clients
const d = t.clients.detail

const titleOf = (client: Client): string =>
  clientName(client) ??
  (client.phone ? formatPhone(client.phone) : client.cardNumber ? c.card(client.cardNumber) : c.unnamed)

const formatDateTime = (iso: string): string =>
  new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    .format(new Date(iso))

function ClientPage() {
  const { orgId, clientId } = Route.useParams()
  const { access } = useCompanyCaller(orgId)
  const can = clientAbilities(access)
  const client = useQuery({ ...clientQuery(orgId, clientId), enabled: can.see })

  if (!can.see) return <NothingForRole />
  if (client.isPending) return <LoadingBlock label={t.app.loading} />
  if (client.isError) {
    const missing = client.error instanceof ApiError && client.error.status === 404
    return (
      <Page>
        <BackLink orgId={orgId} />
        {missing ? (
          <EmptyState icon={<SearchX />} title={d.notFound} message={d.notFoundMessage} />
        ) : (
          <InlineBanner
            kind="error"
            message={errorText(client.error)}
            action={{ label: t.app.retry, onClick: () => void client.refetch() }}
          />
        )}
      </Page>
    )
  }
  return <ClientCard key={client.data.id} orgId={orgId} access={access} can={can} client={client.data} />
}

/// The way back, as the header's quiet eyebrow (PageHeader's `eyebrow`).
const BackLink = ({ orgId }: { orgId: string }) => (
  <Link
    to="/c/$orgId/clients"
    params={{ orgId }}
    className="-ml-1 mb-3 inline-flex min-h-11 items-center gap-0.5 rounded-[8px] pr-1.5 text-caption font-medium text-muted hover:text-text sm:min-h-7"
  >
    <ChevronLeft aria-hidden="true" className="size-4" />
    {c.backToList}
  </Link>
)

/// One key fact under the name — a glyph and a value, no sentence (Attio).
/// Plain text on the page; only a problem (an expired certificate) gets a
/// tinted pill, so the eye lands on it first.
const Fact = ({
  icon,
  tone = 'normal',
  href,
  children,
}: {
  icon: ReactNode
  tone?: 'normal' | 'good' | 'bad'
  href?: string | undefined
  children: ReactNode
}) => {
  const chip = cn(
    'inline-flex h-7 max-w-full items-center gap-1.5 rounded-full text-caption font-medium whitespace-nowrap [&_svg]:size-3.5 [&_svg]:shrink-0',
    tone === 'bad'
      ? 'bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-2.5 text-danger'
      : 'text-text-2 [&_svg]:text-muted',
    tone === 'good' && '[&_svg]:text-success',
    href && 'transition-colors hover:text-text hover:underline hover:underline-offset-2',
  )
  const body = (
    <>
      <span aria-hidden="true" className="contents">
        {icon}
      </span>
      <span className="truncate">{children}</span>
    </>
  )
  return (
    <li className="flex max-w-full shrink-0">
      {href ? (
        <a href={href} className={chip}>
          {body}
        </a>
      ) : (
        <span className={chip}>{body}</span>
      )}
    </li>
  )
}

/// The record's head (Attio, HubSpot): a large avatar, the name, the facts a
/// desk checks first as chips — whether they are in the club right now
/// (`live`), phone, club, the medical certificate (no valid certificate, no
/// entry), since when — and the actions on the right: «Змінити» as a
/// secondary button (editing a person is not the page's creative act, X5),
/// everything rarer in ⋯.
const RecordHeader = ({
  orgId,
  client,
  name,
  branch,
  badge,
  live,
  actions,
}: {
  orgId: string
  client: Client
  name: string
  branch: string | null
  badge: string | undefined
  live?: ReactNode
  actions: ReactNode
}) => {
  const until = client.medicalCertificateUntil
  return (
    <header className="pb-8">
      <BackLink orgId={orgId} />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        {/* Avatar beside the name; the facts under the name on a desktop,
            across the full width on a phone, where the avatar's column
            would squeeze them into one fact per line. */}
        <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-2.5 sm:gap-y-1">
          <Avatar
            name={clientName(client) ?? '·'}
            className="size-12 shrink-0 text-[16px] sm:row-span-2 sm:size-16 sm:text-[20px]"
          />
          <div className="flex min-h-10 min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 sm:self-end">
            <h1 className="text-title font-bold tracking-[-0.015em] break-words text-text">{name}</h1>
            {badge ? (
              <span className="inline-flex h-6 items-center rounded-full px-2 text-caption font-semibold text-text-2 tint-active">
                {badge}
              </span>
            ) : null}
          </div>
          <ul
            aria-label={d.contacts}
            className="col-span-2 flex min-w-0 flex-wrap gap-x-4 gap-y-0.5 sm:col-span-1 sm:col-start-2 sm:self-start"
          >
            {live}
            {client.phone ? (
              <Fact icon={<Phone />} href={`tel:${client.phone}`}>
                <span className="tabular">{formatPhone(client.phone)}</span>
              </Fact>
            ) : null}
            {branch ? <Fact icon={<MapPin />}>{branch}</Fact> : null}
            {client.certificate === 'valid' && until ? (
              <Fact icon={<ShieldCheck />} tone="good">
                {d.chipCertificateValid(formatDate(until))}
              </Fact>
            ) : client.certificate === 'expired' && until ? (
              <Fact icon={<ShieldAlert />} tone="bad">
                {d.chipCertificateExpired(formatDate(until))}
              </Fact>
            ) : (
              <Fact icon={<Shield />}>{d.chipCertificateMissing}</Fact>
            )}
            <Fact icon={<CalendarDays />}>{d.since(formatDate(client.createdAt))}</Fact>
            {client.userId ? <Fact icon={<Smartphone />}>{d.linkedApp}</Fact> : null}
          </ul>
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-1.5 sm:min-h-10">{actions}</div> : null}
      </div>
    </header>
  )
}

function ClientCard({
  orgId,
  access,
  can,
  client,
}: {
  orgId: string
  access: Access
  can: ClientAbilities
  client: Client
}) {
  const search = Route.useSearch()
  const navigate = useNavigate()
  // «Клієнта додано» belongs to this visit only: kept here, and taken out of
  // the address so a reload or a shared link does not announce it again.
  const [created] = useState(search.created)
  useEffect(() => {
    if (search.created) void navigate({ to: '.', search: {}, replace: true })
  }, [search.created, navigate])
  const merged = client.status === 'merged'
  const archived = client.status === 'archived'
  const writable = can.edit && !merged
  const branches = useQuery(branchesQuery(orgId))
  const duplicates = useQuery({ ...duplicatesQuery(orgId, client.id), enabled: can.edit && !merged })
  const update = useUpdateClient(orgId)
  const [editing, setEditing] = useState(false)
  const [erasing, setErasing] = useState(false)
  const [erased, setErased] = useState(false)
  const [merging, setMerging] = useState<Client | null>(null)
  const [news, setNews] = useState<string | null>(null)

  const allBranches = branches.data ?? []
  const showBranch = allBranches.length > 1
  const branchName = client.branchId ? (allBranches.find((b) => b.id === client.branchId)?.name ?? null) : null
  // The client's own club stays offered even when it is archived or outside
  // the caller's clubs — otherwise the form would show nothing ticked.
  const formBranches: readonly Branch[] = (() => {
    const placeable = placeableBranches(access, allBranches)
    const current = allBranches.find((b) => b.id === client.branchId)
    return current && !placeable.includes(current) ? [...placeable, current] : placeable
  })()

  const setStatus = (status: 'active' | 'archived') =>
    update.mutate(
      { clientId: client.id, input: { status } },
      { onSuccess: () => setNews(status === 'archived' ? d.archived : d.restored) },
    )

  const name = titleOf(client)
  const candidates = duplicates.data ?? []
  const showDuplicates = candidates.length > 0 && !archived
  const canMerge = can.manage && showDuplicates && !erased
  // One look-alike: ⋯ opens the merge right away; several: it brings the
  // list of them into view, where each has its own «Обʼєднати».
  const startMerge = () => {
    const [only] = candidates
    if (candidates.length === 1 && only) setMerging(only.client)
    else document.getElementById('duplicates')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  // A merged record has nothing to archive or erase: it is a pointer now.
  const menu =
    writable || (can.manage && !merged && !erased) ? (
      <RowMenu label={`${t.app.more}: ${name}`} trigger={<Ellipsis className="size-5" />}>
        {canMerge ? (
          <RowMenuItem icon={<Merge className="size-4" />} onSelect={startMerge}>
            {d.mergeWith}
          </RowMenuItem>
        ) : null}
        {writable && !archived ? (
          <RowMenuItem icon={<Archive className="size-4" />} onSelect={() => setStatus('archived')}>
            {d.archive}
          </RowMenuItem>
        ) : null}
        {writable && archived ? (
          <RowMenuItem icon={<ArchiveRestore className="size-4" />} onSelect={() => setStatus('active')}>
            {d.restore}
          </RowMenuItem>
        ) : null}
        {can.manage && !merged && !erased ? (
          <RowMenuItem icon={<Eraser className="size-4" />} destructive onSelect={() => setErasing(true)}>
            {c.erase.action}
          </RowMenuItem>
        ) : null}
      </RowMenu>
    ) : null

  return (
    <Page wide>
      <RecordHeader
        orgId={orgId}
        client={client}
        name={name}
        branch={showBranch ? (branchName ?? d.wholeNetwork) : null}
        badge={archived ? c.archivedBadge : merged ? c.mergedBadge : undefined}
        live={<InsideNowFact orgId={orgId} clientId={client.id} clientBranchId={client.branchId} />}
        actions={
          <>
            {writable ? (
              <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
                {d.edit}
              </Button>
            ) : null}
            {menu}
          </>
        }
      />

      <div className="mb-8 flex flex-col gap-3 empty:hidden">
        {created && !erased ? (
          <InlineBanner
            kind={created === 'consents_failed' ? 'stale' : 'done'}
            message={
              created === 'consents_failed' ? `${c.created(name)} ${c.form.consentsFailed}` : c.created(name)
            }
          />
        ) : null}
        {news ? <InlineBanner kind="done" message={news} /> : null}
        {update.isError ? <InlineBanner kind="error" message={clientErrorText(update.error)} /> : null}
        {erased ? <InlineBanner kind="done" title={d.erasedTitle} message={d.erasedMessage} /> : null}
        {merged ? (
          <InlineBanner
            kind="info"
            title={d.mergedTitle}
            message={d.mergedMessage}
            action={
              client.mergedIntoId
                ? {
                    label: d.openSurvivor,
                    onClick: () =>
                      void navigate({
                        to: '/c/$orgId/clients/$clientId',
                        params: { orgId, clientId: client.mergedIntoId ?? '' },
                      }),
                  }
                : undefined
            }
          />
        ) : null}
        {archived && !erased ? (
          <InlineBanner
            kind="info"
            title={d.archivedTitle}
            message={d.archivedMessage}
            action={writable ? { label: d.restore, onClick: () => setStatus('active') } : undefined}
          />
        ) : null}
        {showDuplicates ? (
          <Duplicates orgId={orgId} candidates={candidates} can={can} onMerge={setMerging} />
        ) : null}
      </div>

      {/* The record (Attio): what happens with the person — passes, notes,
          history — in the wide column; who they are on the right. */}
      <div className="grid grid-cols-1 items-start gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-8">
          <ClientPassesSlot
            orgId={orgId}
            clientId={client.id}
            canSell={writable}
            // Just registered: most newcomers buy a pass at the desk right away.
            offerSale={Boolean(created) && !erased}
          />
          {/* Right under the passes: what they spend, and how long it lasted. */}
          <ClientVisits orgId={orgId} clientId={client.id} clientBranchId={client.branchId} />
          <Notes orgId={orgId} access={access} clientId={client.id} writable={writable && !erased} />
          <ClientPurchases orgId={orgId} clientId={client.id} />
          <Timeline orgId={orgId} clientId={client.id} />
        </div>
        <aside className="flex min-w-0 flex-col gap-8" aria-label={d.contacts}>
          <ClientLocker orgId={orgId} clientId={client.id} />
          <Contacts client={client} branchName={branchName} showBranch={showBranch} />
          <Consents orgId={orgId} client={client} writable={writable} />
          {canEnrolFace(access) ? (
            <FaceEntry orgId={orgId} clientId={client.id} consented={client.consents?.biometric === true} />
          ) : null}
        </aside>
      </div>

      {editing ? (
        <EditClientPanel client={client} orgId={orgId} branches={formBranches} onClose={() => setEditing(false)} />
      ) : null}
      {can.manage ? (
        <MergeDialog
          orgId={orgId}
          client={client}
          other={merging}
          onClose={() => setMerging(null)}
          onMerged={() => setNews(c.duplicates.merged)}
        />
      ) : null}
      {can.manage ? (
        <EraseDialog
          open={erasing}
          orgId={orgId}
          client={client}
          onClose={() => setErasing(false)}
          onDone={() => {
            setErasing(false)
            setErased(true)
          }}
        />
      ) : null}
    </Page>
  )
}

// ---- about ----

/// A label / value line: label in a fixed column so the values line up
/// down the card, like a record's attribute list.
const InfoRow = ({ label, children, footnote }: { label: string; children: ReactNode; footnote?: string | undefined }) => (
  <div
    className="compact-row relative grid min-h-11 grid-cols-[132px_minmax(0,1fr)] items-baseline gap-3 px-4 py-2.5"
    style={{ ['--row-inset' as string]: '16px' }}
  >
    <span className="text-caption text-muted">{label}</span>
    <span className="min-w-0 text-callout break-words text-text">
      {children}
      {footnote ? <span className="mt-1 block text-caption text-text-2">{footnote}</span> : null}
    </span>
  </div>
)

const Muted = ({ children }: { children: ReactNode }) => <span className="text-muted">{children}</span>

function Contacts({ client, branchName, showBranch }: { client: Client; branchName: string | null; showBranch: boolean }) {
  const age = client.birthDate ? ageOn(client.birthDate, todayIso()) : null
  const certificate = client.medicalCertificateUntil
  return (
    <SettingsSection id="contacts" title={d.contacts}>
      <InfoRow label={d.phone}>
        {client.phone ? (
          <a href={`tel:${client.phone}`} className="tabular hover:underline">
            {formatPhone(client.phone)}
          </a>
        ) : (
          <Muted>{d.notSet}</Muted>
        )}
      </InfoRow>
      <InfoRow label={d.email}>
        {client.email ? (
          <a href={`mailto:${client.email}`} className="hover:underline">
            {client.email}
          </a>
        ) : (
          <Muted>{d.notSet}</Muted>
        )}
      </InfoRow>
      <InfoRow label={d.birthDate}>
        {client.birthDate ? (
          <>
            {formatDate(client.birthDate)}
            {age !== null ? <Muted> · {d.age(age)}</Muted> : null}
          </>
        ) : (
          <Muted>{d.notSet}</Muted>
        )}
      </InfoRow>
      <InfoRow label={d.cardNumber}>
        {client.cardNumber ? <span className="tabular">{client.cardNumber}</span> : <Muted>{d.notSet}</Muted>}
      </InfoRow>
      {showBranch ? <InfoRow label={d.branch}>{branchName ?? d.wholeNetwork}</InfoRow> : null}
      <InfoRow
        label={d.certificate}
        footnote={client.certificate === 'expired' ? d.certificateFootnote : undefined}
      >
        {certificate && client.certificate === 'valid' ? (
          d.certificateValid(formatDate(certificate))
        ) : certificate && client.certificate === 'expired' ? (
          <span className="text-danger">{d.certificateExpired(formatDate(certificate))}</span>
        ) : (
          <Muted>{d.certificateMissing}</Muted>
        )}
      </InfoRow>
      {client.source ? <InfoRow label={d.source}>{client.source}</InfoRow> : null}
      {client.tags.length > 0 ? (
        <InfoRow label={d.tags}>
          <span className="flex flex-wrap gap-1.5">
            {client.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-surface-3 px-2 py-px text-caption font-medium text-text-2">
                {tag}
              </span>
            ))}
          </span>
        </InfoRow>
      ) : null}
      {client.note ? <InfoRow label={d.note}>{client.note}</InfoRow> : null}
    </SettingsSection>
  )
}

// ---- consents ----

const PURPOSES: readonly ConsentPurpose[] = ['personal_data', 'marketing', 'biometric']

const HINTS: Readonly<Record<ConsentPurpose, string>> = {
  personal_data: c.consents.personal_dataHint,
  marketing: c.consents.marketingHint,
  biometric: c.consents.biometricHint,
}

const consentLine = (event: ConsentEvent | null): string =>
  event === null
    ? c.consents.never
    : event.granted
      ? c.consents.granted(formatDate(event.createdAt))
      : c.consents.withdrawn(formatDate(event.createdAt))

function Consents({ orgId, client, writable }: { orgId: string; client: Client; writable: boolean }) {
  const log = useQuery(consentsQuery(orgId, client.id))
  const record = useRecordConsent(orgId)
  const [history, setHistory] = useState(false)
  const current = client.consents ?? {}
  const events = log.data ?? []

  return (
    <SettingsSection
      id="consents"
      title={c.consents.title}
      meta={
        events.length > 0 ? (
          <button
            type="button"
            aria-expanded={history}
            className="-my-1 rounded-[6px] py-1 font-medium text-text-2 hover:text-text"
            onClick={() => setHistory((v) => !v)}
          >
            {history ? c.consents.hideHistory : c.consents.history}
          </button>
        ) : undefined
      }
      footer={record.isError ? <span className="text-danger">{errorText(record.error)}</span> : c.consents.footnote}
    >
      {PURPOSES.map((purpose) => {
        const busy = record.isPending && record.variables.purpose === purpose
        return (
          <CompactRow
            key={purpose}
            title={c.consents[purpose]}
            subtitle={log.isPending ? HINTS[purpose] : consentLine(latestConsent(events, purpose))}
            actions={
              <span className="pr-2">
                <Switch
                  label={c.consents.toggle(c.consents[purpose])}
                  checked={current[purpose] ?? false}
                  disabled={!writable}
                  busy={busy}
                  onCheckedChange={(granted) => record.mutate({ clientId: client.id, purpose, granted })}
                />
              </span>
            }
          />
        )
      })}
      {history
        ? events.map((event) => (
            <CompactRow
              key={event.id}
              tone="muted"
              title={c.consents.event(c.consents[event.purpose], event.granted)}
              subtitle={[formatDateTime(event.createdAt), c.consents.source[event.source]].filter(Boolean).join(' · ')}
            />
          ))
        : null}
    </SettingsSection>
  )
}

/// A group whose body is not always one card (a composer over a list, a
/// quiet empty line): the same header as SettingsSection, inset to line up
/// with the rows of the cards under it.
const Group = ({ id, title, count, children }: { id: string; title: string; count?: number | undefined; children: ReactNode }) => (
  <section aria-labelledby={`${id}-title`} className="flex flex-col gap-2">
    <SectionHeader id={`${id}-title`} title={title} count={count ? String(count) : undefined} />
    {children}
  </section>
)

const cardClass = 'row-list overflow-hidden rounded-[16px] bg-[var(--card,var(--surface))]'

// ---- notes ----

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/// Notes as a composer (Attio, Linear comments): a card you write in — no
/// grey well inside a card — with «Додати» that wakes up when there is
/// text, and the notes under it, newest first, each with who and when.
function Notes({
  orgId,
  access,
  clientId,
  writable,
}: {
  orgId: string
  access: Access
  clientId: string
  writable: boolean
}) {
  const notes = useQuery(notesQuery(orgId, clientId))
  // Authors by name only for those who may see the staff list.
  const members = useQuery({
    ...membersQuery(orgId),
    enabled: access.god || atLeast(access.permissions.staff, 'view'),
  })
  const add = useAddNote(orgId, clientId)
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const fieldId = useId()

  const author = (note: ClientNote): string => {
    if (note.authorUserId === access.userId) return t.app.you
    const member = members.data?.find((m) => m.userId === note.authorUserId)
    return member?.displayName || c.notes.byColleague
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const text = body.trim()
    if (text === '') return
    if (text.length > 2000) {
      setError(c.notes.tooLong)
      return
    }
    add.mutate(text, {
      onSuccess: () => {
        setBody('')
        setError(undefined)
      },
    })
  }

  const list = notes.data ?? []
  return (
    <Group id="notes" title={c.notes.title} count={list.length}>
      <div className="flex flex-col gap-2">
        {writable ? (
          <form
            onSubmit={submit}
            noValidate
            className={cn(
              'rounded-[16px] bg-[var(--card,var(--surface))] transition-shadow',
              error
                ? 'shadow-[inset_0_0_0_1px_var(--danger)]'
                : 'focus-within:shadow-[inset_0_0_0_1px_var(--border-strong)]',
            )}
          >
            <label htmlFor={fieldId} className="sr-only">
              {c.notes.add}
            </label>
            <textarea
              id={fieldId}
              rows={2}
              placeholder={c.notes.placeholder}
              value={body}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${fieldId}-error` : undefined}
              onChange={(e) => {
                setBody(e.target.value)
                if (error) setError(undefined)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e)
              }}
              className="block max-h-72 min-h-[72px] w-full resize-none bg-transparent px-4 pt-3.5 text-callout text-text outline-none [field-sizing:content] placeholder:text-muted focus-visible:outline-none"
            />
            <div className="flex items-center justify-end gap-3 px-2.5 pt-1 pb-2.5">
              {body.trim() !== '' ? (
                <span aria-hidden="true" className="hidden text-caption text-muted sm:inline">
                  {c.notes.shortcut(isMac)}
                </span>
              ) : null}
              <Button type="submit" variant="secondary" compact busy={add.isPending} disabled={body.trim() === ''}>
                {add.isPending ? c.notes.adding : c.notes.submit}
              </Button>
            </div>
          </form>
        ) : null}
        {error ? (
          <p id={`${fieldId}-error`} className="px-4 text-caption text-danger">
            {error}
          </p>
        ) : null}
        {add.isError ? <InlineBanner kind="error" message={errorText(add.error)} /> : null}
        {notes.isPending ? (
          <LoadingBlock label={t.app.loading} />
        ) : notes.isError ? (
          <InlineBanner kind="error" message={errorText(notes.error)} action={{ label: t.app.retry, onClick: () => void notes.refetch() }} />
        ) : list.length === 0 ? (
          // With a composer the empty box already says «write here» — a
          // second «nothing yet» line would be noise (X8).
          writable ? null : <p className="px-4 text-callout text-muted">{c.notes.empty}</p>
        ) : (
          <div className={cardClass}>
            {list.map((note) => {
              const who = author(note)
              return (
                <article
                  key={note.id}
                  className="compact-row relative px-4 py-3.5"
                  style={{ ['--row-inset' as string]: '16px' }}
                >
                  <p className="flex items-center gap-2 text-caption">
                    <Avatar name={who} className="size-5 text-[9px]" />
                    <span className="font-semibold text-text">{who}</span>
                    <time dateTime={note.createdAt} className="text-muted tabular">
                      {formatDateTime(note.createdAt)}
                    </time>
                  </p>
                  <p className="mt-1.5 pl-7 text-callout whitespace-pre-wrap break-words text-text">{note.body}</p>
                </article>
              )
            })}
          </div>
        )}
      </div>
    </Group>
  )
}

// ---- timeline ----

function Timeline({ orgId, clientId }: { orgId: string; clientId: string }) {
  const timeline = useQuery(timelineQuery(orgId, clientId))
  return (
    <Group id="timeline" title={c.timeline.title}>
      {timeline.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : timeline.isError ? (
        <InlineBanner kind="error" message={errorText(timeline.error)} action={{ label: t.app.retry, onClick: () => void timeline.refetch() }} />
      ) : timeline.data.length === 0 ? (
        // Nothing happened yet is not news: a quiet line, no card (X8).
        <p className="px-4 text-callout text-muted">
          {c.timeline.empty}. <span className="text-caption">{c.timeline.emptyMessage}</span>
        </p>
      ) : (
        <div className={cardClass}>
        {timeline.data.map((entry) => (
          <div
            key={entry.id}
            className="compact-row relative flex min-h-11 items-baseline justify-between gap-4 px-4 py-2.5"
            style={{ ['--row-inset' as string]: '16px' }}
          >
            <span
              className={cn(
                'min-w-0 text-callout',
                entry.kind === 'session_cancelled' || entry.kind === 'cancelled' ? 'text-muted' : 'text-text',
              )}
            >
              {timelineText(entry, c.timeline)}
            </span>
            <time dateTime={entry.happenedAt} className="shrink-0 text-caption text-muted tabular">
              {formatDateTime(entry.happenedAt)}
            </time>
          </div>
        ))}
        </div>
      )}
    </Group>
  )
}

// ---- duplicates and merge ----

/// Look-alike records, with «Обʼєднати» on each; the merge dialog itself
/// lives on the card, so ⋯ → «Обʼєднати з дублікатом» opens the same one.
function Duplicates({
  orgId,
  candidates,
  can,
  onMerge,
}: {
  orgId: string
  candidates: readonly DuplicateCandidate[]
  can: ClientAbilities
  onMerge: (other: Client) => void
}) {
  const navigate = useNavigate()
  return (
    <section id="duplicates" className="scroll-mt-6 rounded-[14px] bg-[color-mix(in_srgb,var(--warning)_12%,transparent)] p-1.5">
      <div className="px-2.5 pt-2 pb-2">
        <p className="text-[14px] leading-5 font-bold text-text">{c.duplicates.title}</p>
        <p className="text-caption text-text-2">
          {c.duplicates.message(candidates.length)} {can.manage ? null : c.duplicates.askManager}
        </p>
      </div>
      <div className="row-list overflow-hidden rounded-[12px] bg-[var(--card,var(--surface))]">
        {candidates.map(({ client: other, reason }) => (
          <CompactRow
            key={other.id}
            leading={<Avatar name={clientName(other) ?? '·'} />}
            title={titleOf(other)}
            subtitle={[
              reason === 'same_phone' ? c.duplicates.samePhone : c.duplicates.sameName,
              other.phone ? formatPhone(other.phone) : null,
              formatDate(other.createdAt),
            ]
              .filter(Boolean)
              .join(' · ')}
            accessory={can.manage ? 'none' : 'chevron'}
            onClick={() =>
              void navigate({ to: '/c/$orgId/clients/$clientId', params: { orgId, clientId: other.id } })
            }
            actions={
              can.manage ? (
                <Button variant="secondary" compact onClick={() => onMerge(other)}>
                  {c.duplicates.merge}
                </Button>
              ) : undefined
            }
          />
        ))}
      </div>
    </section>
  )
}

/// What tells two look-alike records apart: name, contacts, card, when added.
const recordLine = (record: Client): string =>
  [
    titleOf(record),
    record.phone ? formatPhone(record.phone) : null,
    record.email,
    record.cardNumber ? c.card(record.cardNumber) : null,
    c.duplicates.added(formatDateTime(record.createdAt)),
  ]
    .filter(Boolean)
    .join(' · ')

function MergeDialog({
  orgId,
  client,
  other,
  onClose,
  onMerged,
}: {
  orgId: string
  client: Client
  other: Client | null
  onClose: () => void
  onMerged: () => void
}) {
  const merge = useMergeClients(orgId)
  const navigate = useNavigate()
  const [keep, setKeep] = useState<'this' | 'that'>('this')
  return (
    <ConfirmDialog
      open={Boolean(other)}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          merge.reset()
          setKeep('this')
        }
      }}
      destructive={false}
      title={c.duplicates.mergeTitle}
      message={
        <span className="flex flex-col gap-4">
          <span>{c.duplicates.mergeMessage}</span>
          {other ? (
            <span className="flex flex-col gap-2">
              <span className="text-caption font-semibold text-muted">{c.duplicates.keep}</span>
              <NeutralSegmented
                label={c.duplicates.keep}
                onSurface
                value={keep}
                options={[
                  { value: 'this', label: c.duplicates.keepThis },
                  { value: 'that', label: c.duplicates.keepThat },
                ]}
                onChange={setKeep}
                className="flex w-full"
              />
              <span className="text-caption text-text-2">
                {c.duplicates.survivor(recordLine(keep === 'this' ? client : other))}
              </span>
            </span>
          ) : null}
        </span>
      }
      confirmLabel={c.duplicates.mergeConfirm}
      busy={merge.isPending}
      error={merge.isError ? <InlineBanner kind="error" message={clientErrorText(merge.error)} /> : null}
      onConfirm={() => {
        if (!other) return
        const [survivorId, loserId] = keep === 'this' ? [client.id, other.id] : [other.id, client.id]
        merge.mutate(
          { survivorId, loserId },
          {
            onSuccess: () => {
              onClose()
              onMerged()
              if (survivorId !== client.id) {
                void navigate({ to: '/c/$orgId/clients/$clientId', params: { orgId, clientId: survivorId } })
              }
            },
          },
        )
      }}
    />
  )
}

function EraseDialog({
  open,
  orgId,
  client,
  onClose,
  onDone,
}: {
  open: boolean
  orgId: string
  client: Client
  onClose: () => void
  onDone: () => void
}) {
  const erase = useEraseClient(orgId)
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
          erase.reset()
        }
      }}
      title={c.erase.title(titleOf(client))}
      message={c.erase.message}
      confirmLabel={c.erase.confirm}
      busy={erase.isPending}
      error={erase.isError ? <InlineBanner kind="error" message={clientErrorText(erase.error)} /> : null}
      onConfirm={() => erase.mutate(client.id, { onSuccess: onDone })}
    />
  )
}
